import Section from 'app/frontend/base/components/Section'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useTeamApplications } from 'app/frontend/base/config/queries'
import { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'
import { DataTable } from '@/components/ui/data-table'
import {
  date,
  role,
  characterName,
  status,
  links,
} from 'app/frontend/base/config/column-definitions/application'
import {
  itemLevel,
  mythicRating,
  raidParse,
  raidProgress,
} from 'app/frontend/base/config/column-definitions/character'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDifficulties } from 'app/frontend/base/hooks/useDifficulties'
import { CircleCheck, CircleQuestionMark } from 'lucide-react'
import { isUnread } from './utils'
import { useAuth } from 'app/frontend/base/config/context/auth'
import Card from 'app/frontend/base/components/Card'
import LinkButton from '@/components/ui/link-button'
import { useCharacterDetailsStore } from 'app/frontend/base/state/useCharacterDetailsStore'

const TeamApplicants = () => {
  const { team, guild } = useTeam()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { defaultDifficulty } = useDifficulties()
  const [searchParams] = useSearchParams()
  const { accessLevel } = useAuth()
  const { detailsStore, fetchNewDetails, immediatelyFetchNewDetails } =
    useCharacterDetailsStore<wowaudit.Application>(
      (application) => application.characterReference?.id,
    )

  const hasHistoryAccess =
    accessLevel === 'admin' ||
    team.settings.recruitmentApplicationHistoryVisibility.value ===
      'entire_team'

  const { data: pendingApplications } = useTeamApplications(team.id, true)
  const { data: processedApplications, isPending } = useTeamApplications(
    team.id,
    false,
    hasHistoryAccess,
  )

  const pendingColumns: ColumnDef<wowaudit.Application>[] = useMemo(
    () => [
      date,
      role({ withFilters: false }),
      characterName({ withFilters: false }),
      itemLevel<wowaudit.Application>(
        detailsStore,
        (application) => application.characterReference?.id,
      ),
      raidProgress<wowaudit.Application>(
        detailsStore,
        (application) => application.characterReference?.id,
        defaultDifficulty,
      ),
      raidParse<wowaudit.Application>(
        detailsStore,
        (application) => application.characterReference?.id,
        defaultDifficulty,
      ),
      mythicRating<wowaudit.Application>(
        detailsStore,
        (application) => application.characterReference?.id,
      ),
      status({ withFilters: false }),
      links,
    ],
    [detailsStore, defaultDifficulty],
  )

  const processedColumns: ColumnDef<wowaudit.Application>[] = useMemo(
    () => [
      date,
      role({ withFilters: true }),
      characterName({ withFilters: true }),
      itemLevel<wowaudit.Application>(
        detailsStore,
        (application) => application.characterReference?.id,
      ),
      raidProgress<wowaudit.Application>(
        detailsStore,
        (application) => application.characterReference?.id,
        defaultDifficulty,
      ),
      raidParse<wowaudit.Application>(
        detailsStore,
        (application) => application.characterReference?.id,
        defaultDifficulty,
      ),
      mythicRating<wowaudit.Application>(
        detailsStore,
        (application) => application.characterReference?.id,
      ),
      status({ withFilters: true }),
      links,
    ],
    [detailsStore, defaultDifficulty],
  )

  const acceptedCount = useMemo(
    () =>
      processedApplications?.filter(
        (application) => application.status === 'accepted',
      ).length,
    [processedApplications],
  )

  const handleRowClick = (application: wowaudit.Application) => {
    if (isUnread(application)) {
      localStorage.setItem(`application_${application.id}_read`, 'true')
    }

    navigate(`${team.path}/applicants/${application.id}`, {
      state: {
        tableParams: Object.fromEntries(searchParams),
      },
    })
  }

  return (
    <Section title={`Applicants | ${team.name} | ${guild.name}`}>
      <h2 className='flex justify-between items-center flex-wrap gap-2'>
        <span className='flex items-center gap-3'>
          <span>{t('shared.applicants')}</span>
          <Card className='rounded-lg px-3 py-0 text-base'>
            {pendingApplications?.length}
          </Card>
        </span>

        <div className='flex items-center gap-2'>
          {hasHistoryAccess ? (
            <Card
              className={`flex items-center gap-1.5 text-foreground text-base py-[0.3rem] px-3 transition-opacity ${
                isPending ? 'opacity-0' : ''
              }`}
            >
              <span
                className={
                  acceptedCount && acceptedCount > 0
                    ? 'text-success-foreground'
                    : 'text-foreground'
                }
              >
                {acceptedCount ?? 0}
              </span>
              <span>/</span>
              <span>
                {(pendingApplications?.length || 0) +
                  (processedApplications?.length || 0)}
              </span>
              <span className='ml-1 text-foreground font-normal text-sm'>
                accepted
              </span>
            </Card>
          ) : null}

          <LinkButton to={`${team.path}/apply`} size='sm'>
            View form
          </LinkButton>
        </div>
      </h2>

      <div className='mb-6'>
        <DataTable
          className='whitespace-nowrap'
          data={pendingApplications ?? []}
          columns={pendingColumns}
          includeHeader={true}
          onVisibleRowsChange={immediatelyFetchNewDetails}
          emptyMessage={() => (
            <div
              className={`flex flex-col items-center gap-4 my-8 ${
                isPending ? 'opacity-0' : ''
              }`}
            >
              {processedApplications?.length ? (
                <>
                  <CircleCheck size={48} className='text-success-foreground' />{' '}
                  <span>There are no pending applications</span>
                </>
              ) : (
                <>
                  <CircleQuestionMark
                    size={48}
                    className='text-info-foreground'
                  />{' '}
                  <span>
                    Nobody has applied to this team yet. Visit the{' '}
                    <Link
                      to={`${team.path}/settings#recruitment`}
                      className='text-primary hover:text-primary/90 cursor-pointer'
                    >
                      recruitment settings
                    </Link>{' '}
                    to configure a form.
                  </span>
                </>
              )}
            </div>
          )}
          sortingState={[{ id: 'date', desc: true }]}
          getRowId={(application) => application.id.toString()}
          onRowClick={handleRowClick}
          rowClassName={() => `hover:bg-card-nested`}
        />
      </div>

      {hasHistoryAccess ? (
        <div className='my-12'>
          {processedApplications?.length ? (
            <>
              <h2 className='text-xl font-semibold mb-3'>History</h2>
              <DataTable
                className='whitespace-nowrap'
                data={processedApplications ?? []}
                columns={processedColumns}
                includeHeader={true}
                sortingState={[{ id: 'date', desc: true }]}
                getRowId={(application) => application.id.toString()}
                onVisibleRowsChange={fetchNewDetails}
                pagination={20}
                filterType='includesString'
                onRowClick={handleRowClick}
                rowClassName={() => 'hover:bg-card-nested'}
              />
            </>
          ) : null}
        </div>
      ) : null}
    </Section>
  )
}

export default TeamApplicants
